// client/src/api/api.js
import axios from 'axios';
import { SERVER_URL } from '../constants/server-type';
import { readScope } from '../state/scopeStorage';

// -----------------------------
// LocalStorage helpers
// -----------------------------
const TOKEN_KEY = 'accessToken';
const ORG_KEY = 'activeOrgId';
const PROJECT_KEY = 'activeProjectId';


export const getAccessToken = () =>
  (typeof window !== 'undefined' ? localStorage.getItem(TOKEN_KEY) : null);

const setAccessToken = (t) => {
  if (typeof window === 'undefined') return;
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
  try { localStorage.setItem('token-update', String(Date.now())); } catch {}
};

export const getActiveOrgId = () =>
  (typeof window !== 'undefined' ? localStorage.getItem(ORG_KEY) : null);

export const setActiveOrgId = (id) => {
  if (typeof window === 'undefined') return;
  if (id) localStorage.setItem(ORG_KEY, id);
  else localStorage.removeItem(ORG_KEY);
};

export const getActiveProjectId = () =>
  (
    typeof window !== 'undefined'
      ? localStorage.getItem(PROJECT_KEY)
      : null
  );

export const setActiveProjectId = (id) => {

  if (typeof window === 'undefined') return;

  if (id) {
    localStorage.setItem(
      PROJECT_KEY,
      id
    );
  } else {
    localStorage.removeItem(
      PROJECT_KEY
    );
  }
};

// -----------------------------
// Axios instance (cookies enabled)
// -----------------------------
const api = axios.create({
  baseURL: SERVER_URL,
  withCredentials: true,
});

// Build headers (supports explicit X-Org-Id override and omit)
const attachScopeHeaders = (
  headers = {},
  orgIdOverride,
  projectIdOverride
) => {

  const out = {
    'Content-Type': 'application/json',
    ...headers
  };

  // -----------------------------------
  // TOKEN
  // -----------------------------------

  const token = getAccessToken();

  if (token) {
    out.Authorization = `Bearer ${token}`;
  }

  // -----------------------------------
  // ORG
  // -----------------------------------

  const hasExplicitOrg =
    Object.prototype.hasOwnProperty.call(
      out,
      'X-Org-Id'
    );

  const explicitOrg =
    hasExplicitOrg
      ? out['X-Org-Id']
      : undefined;

  const omitOrg =
    explicitOrg === '__omit__' ||
    explicitOrg === '';

  if (!omitOrg && !hasExplicitOrg) {

    const scope = readScope();

    const orgId =
      orgIdOverride ||

      scope?.org?.id ||

      getActiveOrgId() ||

      (
        typeof window !== 'undefined'
          ? localStorage.getItem('activeOrg')
          : null
      );

    if (orgId) {
      out['X-Org-Id'] =
        String(orgId);
    }
  }

  // -----------------------------------
  // PROJECT
  // -----------------------------------

  const hasExplicitProject =
    Object.prototype.hasOwnProperty.call(
      out,
      'X-Project-Id'
    );

  const explicitProject =
    hasExplicitProject
      ? out['X-Project-Id']
      : undefined;

  const omitProject =
    explicitProject === '__omit__' ||
    explicitProject === '';

  if (
    !omitProject &&
    !hasExplicitProject
  ) {

    const projectId =

      projectIdOverride ||

      getActiveProjectId();

    if (projectId) {

      out['X-Project-Id'] =
        String(projectId);
    }
  }

  return out;
};


// -----------------------------
// Cookie-based refresh for fetch flows
// -----------------------------
let fetchRefreshPromise = null;

export async function refreshAccessToken() {

  console.log(
    '[REFRESH] calling',
    `${SERVER_URL}/auth/refresh`
  );

  const res = await fetch(
    `${SERVER_URL}/auth/refresh`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type':
          'application/json'
      },
      body: '{}',
    }
  );

  console.log(
    '[REFRESH] status:',
    res.status
  );

  if (!res.ok) {
    throw new Error(
      `Refresh failed: ${res.status}`
    );
  }

  const data =
    await res.json();

  setAccessToken(
    data.accessToken
  );

  return data.accessToken;
}

async function ensureFetchRefreshed() {
  if (!fetchRefreshPromise) {
    fetchRefreshPromise = refreshAccessToken().finally(() => {
      fetchRefreshPromise = null;
    });
  }
  return fetchRefreshPromise;
}

// -----------------------------
// fetch-based helper (401-aware + retry once)
// -----------------------------
export async function apiFetch(path, opts = {}) {
  // console.log("SERVER_URL:", SERVER_URL)
  const { method = 'GET', headers = {}, body, signal, orgIdOverride, projectIdOverride } = opts;

  // If there is no token yet, try one proactive refresh (first page load case)
  if (!getAccessToken()) {
    try { await ensureFetchRefreshed(); } catch { /* ignore; server might allow public */ }
  }

  const doFetch = async () => {
    const finalHeaders = attachScopeHeaders(headers, orgIdOverride, projectIdOverride);
    // console.log("finalHeaders:", finalHeaders)
    const res = await fetch(`${SERVER_URL}${path}`, {
      method,
      headers: finalHeaders,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include',
      signal,
    });
    return res;
  };

  let res = await doFetch();

  // Retry once on 401 by refreshing the token
  if (res.status === 401)
    console.error('[401 DETECTED]', {
      path,
      status: res.status,
      hostname: window.location.hostname
      });
  if (res.status === 401 && path !== '/auth/login' && opts.retryAuth !== false) {

    try {
      await ensureFetchRefreshed();
      res = await doFetch();
    } catch (err) {

      console.error(
        'Refresh failed',
        err
      );

      clearAccessToken();

      window.location.href =
        '/login';

      throw err;
    }
  }

  // Try to parse JSON safely (even for empty bodies)
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { /* non-JSON error pages */ }

  if (!res.ok) {
    const err = new Error(data?.message || res.statusText);
    err.response = { status: res.status, data };
    throw err;
  }
  return data;
}

// -----------------------------
// (Optional) Axios interceptors (unchanged, if you use axios elsewhere)
// -----------------------------
let isRefreshing = false;
let pendingQueue = [];

const processQueue = (error, token = null) => {
  pendingQueue.forEach(({ resolve, reject, config }) => {
    if (error) reject(error);
    else {
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      resolve(api(config));
    }
  });
  pendingQueue = [];
};

api.interceptors.request.use(
  (config) => {
    config.headers = attachScopeHeaders(config.headers || {}, /*orgOverride*/ undefined);
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error || {};
    if (!response || !config) return Promise.reject(error);
    if (response.status === 403) {
      // Forbidden: user is authenticated but lacks permission
      return Promise.reject(error);
    }

    if (response.status !== 401 || config._retry) {
      return Promise.reject(error);
    }
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject, config: { ...config, _retry: true } });
      });
    }

    config._retry = true;
    isRefreshing = true;

    try {
      const newToken = await refreshAccessToken();
      processQueue(null, newToken);
      isRefreshing = false;

      const retried = { ...config };
      retried.headers = attachScopeHeaders(retried.headers || {}, undefined);
      return api(retried);
    } catch (e) {
      processQueue(e, null);
      isRefreshing = false;
      try { await api.post('/auth/logout'); } catch {}
      setAccessToken(null);
      if (typeof window !== 'undefined') window.location.href = '/login';
      return Promise.reject(e);
    }
  }
);

// -----------------------------
// Auth utils
// -----------------------------
export const clearAccessToken = () => setAccessToken(null);
export const isAuthenticated = () => !!getAccessToken();

export const refreshToken = async () => {
  const token = await refreshAccessToken();
  return token;
};

export const logout = async () => {
  try { await api.post('/auth/logout'); } catch {}
  setAccessToken(null);
  if (typeof window !== 'undefined') window.location.href = '/login';
};

export default api;
